<p-toast [style]="{marginTop: '80px'}"></p-toast>
@if (formLoading) {
  <div class="progress-spinner">
    <app-spinner [formLoading]="formLoading"></app-spinner>
  </div>
}

<h2>Future Occupancy Report</h2>

@if (showSearchUI) {
  <app-search-template
    searchLabel="Search"
    [showSearchButton]=true
    [reset]=true
    [showLocations]=true
    [showDevelopments]=true
    [showShortTermRentals]=true
    [showRateBuckets]=false
    [showBedrooms]=true
    [showSeasons]=false
    [showResTypes]=true
    [showStartDate]=true
    [showEndDate]=true
    [defaultStartDate]="defaultStartDate"
    [defaultEndDate]="defaultEndDate"
    (doSearchCB)="onSearchSubmit($event)">
  </app-search-template>
}

<!-- KPI Cards -->
@if (reservations.length > 0 || monthlyData.length > 0) {
  <div class="kpi-grid">
    <div class="kpi-card"
      pTooltip="Total number of reservations that overlap with the selected date range"
      tooltipPosition="bottom">
      <p class="kpi-label">TOTAL RESERVATIONS</p>
      <p class="kpi-value">{{ totalReservations | number:'1.0-0' }}</p>
    </div>
    <div class="kpi-card"
      pTooltip="Sum of nights within the selected date range across all reservations — partial reservations are clamped to the search window"
      tooltipPosition="bottom">
      <p class="kpi-label">BOOKED NIGHTS</p>
      <p class="kpi-value">{{ bookedNights | number:'1.0-0' }}</p>
    </div>
    <div class="kpi-card"
      pTooltip="Weighted average occupancy rate = total booked nights ÷ total available nights × 100. Available nights = all active properties (regardless of bookings) × days in range per month"
      tooltipPosition="bottom">
      <p class="kpi-label">AVG OCCUPANCY</p>
      <p class="kpi-value">{{ avgOccupancyPct }}%</p>
    </div>
    @if (canViewRevenue) {
      <div class="kpi-card"
        pTooltip="Total revenue within the selected date range — Owner Amount (product 105) + KBM Management Fee (product 106), pro-rated by nights falling inside the search window"
        tooltipPosition="bottom">
        <p class="kpi-label">REVENUE</p>
        <p class="kpi-value">{{ totalRevenue | currency:'USD':'symbol':'1.0-0' }}</p>
      </div>
    }
  </div>
}

<!-- Tabs -->
@if (reservations.length > 0 || monthlyData.length > 0) {
  <div class="tab-bar">
    <button [class.active]="activeTab === 'overview'"     (click)="activeTab = 'overview'">Overview</button>
    <!-- <button [class.active]="activeTab === 'properties'"   (click)="activeTab = 'properties'">Properties</button> -->
    <button [class.active]="activeTab === 'reservations'" (click)="activeTab = 'reservations'">Reservations</button>
  </div>
}

<!-- ====================================================
OVERVIEW TAB
===================================================== -->
@if (activeTab === 'overview' && monthlyData.length > 0) {
  <div>
    <div class="chart-row">
      <!-- Monthly Occupancy Rate Bar Chart -->
      <div class="chart-card">
        <h3>Occupancy Rate (%) @if (chartDateRangeLabel) {
          <span>For {{ chartDateRangeLabel }}</span>
        }</h3>
        <p-chart type="bar" [data]="barChartData" [options]="barChartOptions" height="220px"></p-chart>
      </div>
      <!-- Revenue Line Chart — role 1000 only -->
      @if (canViewRevenue) {
        <div class="chart-card">
          <h3>Revenue ($)</h3>
          <p-chart type="line" [data]="lineChartData" [options]="lineChartOptions" height="220px"></p-chart>
        </div>
      }
    </div>
    <!-- Row 2: Occupancy by Type (pie, left) + Check-ins & Check-outs daily (right) -->
    @if (byTypeData.length > 0) {
      <div class="chart-row">
        <!-- Pie chart — occupancy share by type -->
        <div class="chart-card">
          <h3>Occupancy Share by Type</h3>
          <div class="pie-layout">
            <div class="pie-chart-wrap">
              <p-chart type="pie" [data]="pieChartData" [options]="pieChartOptions" [plugins]="piePlugins" height="200px"></p-chart>
            </div>
            <div class="pie-legend">
              <table class="legend-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Res. Type</th>
                    <th># of Nights</th>
                  </tr>
                </thead>
                <tbody>
                  @for (t of byTypeData; track t; let i = $index) {
                    <tr>
                      <td><span class="legend-dot" [style.background]="typeColorMap[t.resTypeName] || '#6366f1'"></span></td>
                      <td>{{ t.resTypeName }}</td>
                      <td class="font-bold">{{ t.numNights }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- Daily Check-ins & Check-outs (moved from row 3) -->
        <div class="chart-card">
          <h3>Check-ins &amp; Check-outs</h3>
          <p class="chart-subtitle">Daily check-in / check-out counts within selected date range</p>
          <div class="daily-legend">
            <span class="daily-legend-item">
              <span class="daily-line" style="background:#2563eb;"></span>
              <span class="daily-dot" style="border-color:#2563eb;"></span>
              Check-ins
            </span>
            <span class="daily-legend-item">
              <span class="daily-line" style="background:#dc2626;"></span>
              <span class="daily-dot" style="border-color:#dc2626;"></span>
              Check-outs
            </span>
          </div>
          <p-chart type="line" [data]="dailyChartData" [options]="dailyChartOptions" height="220px"></p-chart>
        </div>
      </div>
    }
    <!-- Row 3: Check-ins by Unit Type + Check-outs by Unit Type (multi-line by bedroom) -->
    <div class="chart-row">
      <div class="chart-card">
        <h3>Check-ins by Unit Type</h3>
        <p class="chart-subtitle">Daily check-in volume by bedroom category</p>
        <p-chart type="line" [data]="ciByBedroomChartData" [options]="ciByBedroomChartOptions" height="240px"></p-chart>
      </div>
      <div class="chart-card">
        <h3>Check-outs by Unit Type</h3>
        <p class="chart-subtitle">Daily check-out volume by bedroom category</p>
        <p-chart type="line" [data]="coByBedroomChartData" [options]="coByBedroomChartOptions" height="240px"></p-chart>
      </div>
    </div>
  </div>
}

<!-- ====================================================
PROPERTIES TAB
===================================================== -->
@if (activeTab === 'properties' && reservations.length > 0) {
  <div>
    <div class="chart-card full-width">
      <h3>Booked Nights by Property</h3>
      <table class="data-table">
        <thead>
          <tr>
            <th>Property</th>
            <th class="align-right">Booked Nights</th>
            <th class="align-right">Reservations</th>
            <th class="align-right">Revenue</th>
          </tr>
        </thead>
        <tbody>
          @for (prop of properties; track prop) {
            <tr>
              <td class="font-medium">{{ prop }}</td>
              <td class="align-right">{{ reservations | sumField:'numNights':prop }}</td>
              <td class="align-right">{{ (reservations | filterByProp:prop).length }}</td>
              <td class="align-right revenue-cell">{{ reservations | sumRevenue:prop | currency:'USD':'symbol':'1.0-0' }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
}

<!-- ====================================================
RESERVATIONS TAB
===================================================== -->
@if (activeTab === 'reservations') {
  <div>
    <!-- External filter dropdowns — sit above the grid, same style as other reports -->
    <div class="table-header">
      <h3 class="res-grid-title">All Upcoming Reservations</h3>
      <div class="filter-group">
        <p-multiSelect
          [options]="properties"
          [(ngModel)]="propertyFilters"
          defaultLabel="All Properties"
          [showToggleAll]="true"
          display="chip"
          [scrollHeight]="'50vh'"
          [style]="{'minWidth':'200px'}"
          appendTo="body"
          (onChange)="onFilterChange()">
        </p-multiSelect>
        <p-multiSelect
          [options]="resTypeOptions"
          [(ngModel)]="resTypeFilters"
          defaultLabel="All Types"
          [showToggleAll]="true"
          display="chip"
          [scrollHeight]="'50vh'"
          [style]="{'minWidth':'200px'}"
          appendTo="body"
          (onChange)="onFilterChange()">
        </p-multiSelect>
      </div>
    </div>
    <ag-grid-angular
      style="width: auto; height: 70vh;"
      class="ag-theme-balham"
      [gridOptions]="gridOptions"
      [rowData]="reservations"
      [columnDefs]="columnDefs"
      [defaultColDef]="defaultColDef"
      (gridReady)="onGridReady($event)"
      (firstDataRendered)="onFirstDataRendered()">
    </ag-grid-angular>
  </div>
}
